import { useEffect, useMemo, useState, useCallback, useLayoutEffect } from 'react';
import * as anchor from '@project-serum/anchor';
import { BackToTop } from "material-ui-back-to-top";
import { HashLink } from 'react-router-hash-link';
import axios from 'axios';

// COMPONENTS
import Header from './components/Header';
import Footer from './components/Footer';

// MUI
import styled from 'styled-components';
import { Container, Snackbar } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Alert from '@material-ui/lab/Alert';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';

// images
import mysteryBunny from "./assets/mystery-nft.jpg";

// candymachine stuff
import { Connection, programs } from '@metaplex/js'; 
import { PublicKey } from '@solana/web3.js';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletDialogButton } from '@solana/wallet-adapter-material-ui';
import {
  awaitTransactionSignatureConfirmation,
  CandyMachineAccount,
  CANDY_MACHINE_PROGRAM,
  getCandyMachineState,
  mintOneToken,
} from './candy-machine';
import { AlertState, toDate, formatNumber } from './utils';
import { MintCountdown } from './MintCountdown';
import { MintButton } from './MintButton';
import { GatewayProvider } from '@civic/solana-gateway-react';
import { ModalContainer } from './components/Modal';
import { useHomeStyles } from './styles/home';
import { RSVPForm } from './components/rsvpForm';
import { Rewards } from './components/Rewards';
import { HomeContent } from './components/HomeContent';
import { HeroFeatures } from './components/HeroFeatures';
import LinearIndeterminate from './components/LinearProgress';

const ConnectButton = styled(WalletDialogButton)`
  width: 100%;
  height: 60px;
  margin-top: 10px;
  margin-bottom: 5px;
  background: linear-gradient(180deg, #604ae5 0%, #813eee 100%);
  color: white;
  font-size: 16px;
  font-weight: bold;
`;

const MintContainer = styled.div``; // add your owns styles here

export interface HomeProps {
  candyMachineId?: anchor.web3.PublicKey;
  connection: anchor.web3.Connection;
  txTimeout: number;
  rpcHost: string;
}

const Home = (props: HomeProps) => {
  const [isUserMinting, setIsUserMinting] = useState(false);
  const [candyMachine, setCandyMachine] = useState<CandyMachineAccount>();
  const [alertState, setAlertState] = useState<AlertState>({
    open: false,
    message: '',
    severity: undefined,
  });
  const [isActive, setIsActive] = useState(false);
  const [endDate, setEndDate] = useState<Date>();
  const [itemsRemaining, setItemsRemaining] = useState<number>();
  //const [isWhitelistUser, setIsWhitelistUser] = useState(false);
  const isWhitelistUser = false;
  const [isPresale, setIsPresale] = useState(false);
  const [discountPrice, setDiscountPrice] = useState<anchor.BN>();
  const [hasGatedMint, setHasGatedMint] = useState<string>('false');
  const [verifiedNft, setVerifiedNft] = useState<string>('');
  const rpcUrl = props.rpcHost;
  const wallet = useWallet();

  const anchorWallet = useMemo(() => {
    if (
      !wallet ||
      !wallet.publicKey ||
      !wallet.signAllTransactions ||
      !wallet.signTransaction
    ) {
      return;
    }

    return {
      publicKey: wallet.publicKey,
      signAllTransactions: wallet.signAllTransactions,
      signTransaction: wallet.signTransaction,
    } as anchor.Wallet;
  }, [wallet]);

  const fetchMintedTokens = async () => {
    try {
      setIsLoading(true);
      const result = await axios.get('https://lasvegasvp.com/api/get-current-minted.php');
      const data = await result.data;
      setIsLoading(false);
      return data;
    } catch(error) {
      console.log('error');

      return [];
    }
  };

  const refreshCandyMachineState = useCallback(async () => {
    if (!anchorWallet) {
      return;
    }

    if (hasGatedMint === 'false' && anchorWallet) {
      try {
        const { metadata: { Metadata } } = programs;
        const connection = window.location.host === 'nft.lasvegasvp.com' ? new Connection('mainnet-beta') : new Connection('devnet');
        const nftsmetadata = await Metadata.findDataByOwner(connection, anchorWallet.publicKey);
        const currentMinted = await fetchMintedTokens();
        
        nftsmetadata.forEach((elm) => {
          const result = currentMinted.find((curr: string) => curr === elm.mint);

          if (result) {
            setHasGatedMint('true');
            setVerifiedNft(result);
            return;
          }
        });
      } catch (e) {
        console.log('error', e);
      }
    }

    if (props.candyMachineId) {
      try {
        const cndy = await getCandyMachineState(
          anchorWallet,
          props.candyMachineId,
          props.connection,
        );
        let active =
          cndy?.state.goLiveDate?.toNumber() < new Date().getTime() / 1000;
        let presale = false;
        // whitelist mint?
        if (cndy?.state.whitelistMintSettings) {
          // is it a presale mint?
          if (
            cndy.state.whitelistMintSettings.presale &&
            (!cndy.state.goLiveDate ||
              cndy.state.goLiveDate.toNumber() > new Date().getTime() / 1000)
          ) {
            presale = true;
          }
          // is there a discount?
          if (cndy.state.whitelistMintSettings.discountPrice) {
            setDiscountPrice(cndy.state.whitelistMintSettings.discountPrice);
          } else {
            setDiscountPrice(undefined);
            // when presale=false and discountPrice=null, mint is restricted
            // to whitelist users only
            if (!cndy.state.whitelistMintSettings.presale) {
              cndy.state.isWhitelistOnly = true;
            }
          }
          // retrieves the whitelist token
          // const mint = new anchor.web3.PublicKey(
          //   cndy.state.whitelistMintSettings.mint,
          // );
          //const token = (await getAtaForMint(mint, anchorWallet.publicKey))[0];
          
          // TODO: move this custom to check wallet nfts to redeem.tsx
          // try {
          //   // lol
          //     const { metadata: { Metadata } } = programs;
          //     const connection = new Connection('devnet'); // mainnet-beta
          //     const ownerPublickey = 'A717cQQqaqLjSc2uqZLdfB7whZm75uWYFyGfBo3VMge4';
          //     const nftsmetadata = await Metadata.findDataByOwner(connection, anchorWallet.publicKey);
            
          //     console.log('nftsmetadata', nftsmetadata);
          // } catch (e) {
          //   console.log('error', e);
          // }
          // comment out below try if need whitelist features
          // try {
          //   const balance = await props.connection.getTokenAccountBalance(
          //     token,
          //   );
          //   let valid = parseInt(balance.value.amount) > 0;
          //   // only whitelist the user if the balance > 0
          //   setIsWhitelistUser(valid);
          //   active = (presale && valid) || active;
          // } catch (e) {
          //   setIsWhitelistUser(false);
          //   // no whitelist user, no mint
          //   if (cndy.state.isWhitelistOnly) {
          //     active = false;
          //   }
          //   console.log('There was a problem fetching whitelist token balance');
          //   console.log(e);
          // }
        }
        // datetime to stop the mint?
        if (cndy?.state.endSettings?.endSettingType.date) {
          setEndDate(toDate(cndy.state.endSettings.number));
          if (
            cndy.state.endSettings.number.toNumber() <
            new Date().getTime() / 1000
          ) {
            active = false;
          }
        }
        // amount to stop the mint?
        if (cndy?.state.endSettings?.endSettingType.amount) {
          let limit = Math.min(
            cndy.state.endSettings.number.toNumber(),
            cndy.state.itemsAvailable,
          );
          if (cndy.state.itemsRedeemed < limit) {
            setItemsRemaining(limit - cndy.state.itemsRedeemed);
          } else {
            setItemsRemaining(0);
            cndy.state.isSoldOut = true;
          }
        } else {
          setItemsRemaining(cndy.state.itemsRemaining);
        }

        if (cndy.state.isSoldOut) {
          active = false;
        }

        setIsActive((cndy.state.isActive = active));
        setIsPresale((cndy.state.isPresale = presale));
        setCandyMachine(cndy);
      } catch (e) {
        console.log('There was a problem fetching Candy Machine state');
        console.log(e);
      }
    }
  }, [anchorWallet, props.candyMachineId, props.connection, hasGatedMint]);

  const onMint = async () => {
    try {
      setIsUserMinting(true);
      document.getElementById('#identity')?.click();
      if (wallet.connected && candyMachine?.program && wallet.publicKey) {
        const mintTxId = (
          await mintOneToken(candyMachine, wallet.publicKey)
        )[0];

        let status: any = { err: true };
        if (mintTxId) {
          status = await awaitTransactionSignatureConfirmation(
            mintTxId,
            props.txTimeout,
            props.connection,
            true,
          );
        }

        if (status && !status.err) {
          // manual update since the refresh might not detect
          // the change immediately
          let remaining = itemsRemaining! - 1;
          setItemsRemaining(remaining);
          setIsActive((candyMachine.state.isActive = remaining > 0));
          candyMachine.state.isSoldOut = remaining === 0;
          setAlertState({
            open: true,
            message: 'Congratulations! Mint succeeded!',
            severity: 'success',
          });
        } else {
          setAlertState({
            open: true,
            message: 'Mint failed! Please try again!',
            severity: 'error',
          });
        }
      }
    } catch (error: any) {
      let message = error.msg || 'Minting failed! Please try again!';
      if (!error.msg) {
        if (!error.message) {
          message = 'Transaction Timeout! Please try again.';
        } else if (error.message.indexOf('0x137')) {
          message = `SOLD OUT!`;
        } else if (error.message.indexOf('0x135')) {
          message = `Insufficient funds to mint. Please fund your wallet.`;
        }
      } else {
        if (error.code === 311) {
          message = `SOLD OUT!`;
          window.location.reload();
        } else if (error.code === 312) {
          message = `Minting period hasn't started yet.`;
        }
      }

      setAlertState({
        open: true,
        message,
        severity: 'error',
      });
      // updates the candy machine state to reflect the lastest
      // information on chain
      refreshCandyMachineState();
    } finally {
      setIsUserMinting(false);
    }
  };

  const toggleMintButton = () => {
    let active = !isActive || isPresale;

    if (active) {
      if (candyMachine!.state.isWhitelistOnly && !isWhitelistUser) {
        active = false;
      }
      if (endDate && Date.now() >= endDate.getTime()) {
        active = false;
      }
    }

    if (
      isPresale &&
      candyMachine!.state.goLiveDate &&
      candyMachine!.state.goLiveDate.toNumber() <= new Date().getTime() / 1000
    ) {
      setIsPresale((candyMachine!.state.isPresale = false));
    }

    setIsActive((candyMachine!.state.isActive = active));
  };

  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  });
  
  useEffect(() => {
    refreshCandyMachineState();
  }, [
    anchorWallet,
    props.candyMachineId,
    props.connection,
    refreshCandyMachineState,
  ]);
  
  interface DefaultValues {
    [key: string]: string
    name: string
    twitch: string
    email: string
    minted: string
    subject: string
  }
  
  const defaultValues = {
    name: '',
    twitch: '',
    email: '',
    minted: '',
    subject: '',
  };

  const classes = useHomeStyles(); // CUSTOM STYLES FOR PAGE
  const [open, setOpen] = useState(false);   // modal
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div className={classes.root}>
      <Header />

      <Paper elevation={0} className={classes.hero}>
        <Container style={{ padding: '30px 2%' }}>
          <Grid container spacing={3}>
            {isLoading &&
              <LinearIndeterminate />  
            }
            {hasGatedMint === 'true' &&
              <>
                <ModalContainer 
                  open={open}
                  handleClose={handleClose}
                >
                  <RSVPForm 
                    handleClose={handleClose}
                    minted={verifiedNft}
                    subject={'RSVP Next Event'}
                  />
                </ModalContainer>

                <Rewards 
                  handleOpen={handleOpen}
                />
              </>
            }
            <Grid item xs={12} md={6}>
              <Paper className={classes.paper}>
                <Typography variant="h3" paragraph style={{ fontWeight: 'bold', fontSize: '2.2rem' }}>
                  Collectible VP Bunny NFTs
                </Typography>
                <Typography variant="h5" paragraph style={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                  Only 2,222 VP Bunnies will ever be minted!<br />Used as special event pass, archive access, and more
                </Typography>
                <Typography paragraph>
                  VP Bunny NFTs offer all early fans of LasVegasVP a chance to collect unique VP Bunny art and gain access to special aspects of LasVegasVP stream and VP himself. A MUST have for fans of VP and NFT collectors alike.
                </Typography>

                <Container style={{ padding: 0, marginTop: 30 }}>
                  <HashLink smooth to={'/#bunnyrecruiter'}>
                    <Button variant="contained" color="primary" style={{ marginRight: 30, textTransform: 'capitalize' }} className={classes.recruitNowButton}>
                      Recruit Now
                    </Button>
                  </HashLink>
                  <HashLink smooth to={'/#perks'}>
                    <Button variant="contained" style={{ textTransform: 'capitalize' }}>
                      Learn More
                    </Button>
                  </HashLink>
                </Container>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6} style={{ padding: 0 }}>
              <Paper className={classes.paper}>
                {/* START OF MINTING COMPONENT */}
                <Container maxWidth="xs" style={{ position: 'relative' }} className="innerhalp" id="bunnyrecruiter">
                  <Paper
                    style={{
                      padding: 20,
                      paddingBottom: 10,
                      backgroundColor: '#151A1F',
                      borderRadius: 6,
                    }}
                  >
                    <img src={mysteryBunny} style={{ width: '100%' }} alt="Mystery Bunny NFT" />
                  <Typography
                      variant="caption"
                      align="center"
                      display="block"
                      style={{ marginTop: 7, color: 'white' }}
                    >
                    <b style={{ fontSize: '1.5rem' }}>Bunny Recruiter</b>
                  </Typography>
                    {!wallet.connected ? (
                      <ConnectButton>Connect Wallet</ConnectButton>
                    ) : (
                      <>
                        {candyMachine && (
                          <Grid
                            container
                            direction="row"
                            justifyContent="center"
                            wrap="nowrap"
                          >
                            <Grid item xs={3}>
                              <Typography variant="body2" color="textSecondary">
                                Remaining
                              </Typography>
                              <Typography
                                variant="h6"
                                color="textPrimary"
                                style={{
                                  fontWeight: 'bold',
                                }}
                              >
                                {`${itemsRemaining}`}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" color="textSecondary">
                                {isWhitelistUser && discountPrice
                                  ? 'Discount Price'
                                  : 'Price'}
                              </Typography>
                              <Typography
                                variant="h6"
                                color="textPrimary"
                                style={{ fontWeight: 'bold' }}
                              >
                                {isWhitelistUser && discountPrice
                                  ? `◎ ${formatNumber.asNumber(discountPrice)}`
                                  : `◎ ${formatNumber.asNumber(
                                      candyMachine.state.price,
                                    )}`}
                              </Typography>
                            </Grid>
                            <Grid item xs={5}>
                              {isActive && endDate && Date.now() < endDate.getTime() ? (
                                <>
                                  <MintCountdown
                                    key="endSettings"
                                    date={getCountdownDate(candyMachine)}
                                    style={{ justifyContent: 'flex-end' }}
                                    status="COMPLETED"
                                    onComplete={toggleMintButton}
                                  />
                                  <Typography
                                    variant="caption"
                                    align="center"
                                    display="block"
                                    style={{ fontWeight: 'bold' }}
                                  >
                                    TO END OF MINT
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  <MintCountdown
                                    key="goLive"
                                    date={getCountdownDate(candyMachine)}
                                    style={{ justifyContent: 'flex-end' }}
                                    status={
                                      candyMachine?.state?.isSoldOut ||
                                      (endDate && Date.now() > endDate.getTime())
                                        ? 'COMPLETED'
                                        : isPresale
                                        ? 'PRESALE'
                                        : 'LIVE'
                                    }
                                    onComplete={toggleMintButton}
                                  />
                                  {isPresale &&
                                    candyMachine.state.goLiveDate &&
                                    candyMachine.state.goLiveDate.toNumber() >
                                      new Date().getTime() / 1000 && (
                                      <Typography
                                        variant="caption"
                                        align="center"
                                        display="block"
                                        style={{ fontWeight: 'bold' }}
                                      >
                                        UNTIL PUBLIC MINT
                                      </Typography>
                                    )}
                                </>
                              )}
                            </Grid>
                          </Grid>
                        )}
                        <MintContainer>
                          {candyMachine?.state.isActive &&
                          candyMachine?.state.gatekeeper &&
                          wallet.publicKey &&
                          wallet.signTransaction ? (
                            <GatewayProvider
                              wallet={{
                                publicKey:
                                  wallet.publicKey ||
                                  new PublicKey(CANDY_MACHINE_PROGRAM),
                                //@ts-ignore
                                signTransaction: wallet.signTransaction,
                              }}
                              gatekeeperNetwork={
                                candyMachine?.state?.gatekeeper?.gatekeeperNetwork
                              }
                              clusterUrl={rpcUrl}
                              options={{ autoShowModal: false }}
                            >
                              <MintButton
                                candyMachine={candyMachine}
                                isMinting={isUserMinting}
                                onMint={onMint}
                                isActive={isActive || (isPresale && isWhitelistUser)}
                              />
                            </GatewayProvider>
                          ) : (
                            <MintButton
                              candyMachine={candyMachine}
                              isMinting={isUserMinting}
                              onMint={onMint}
                              isActive={isActive || (isPresale && isWhitelistUser)}
                            />
                          )}
                        </MintContainer>
                      </>
                    )}
                    
                  </Paper>
                </Container>
                {/* END OF MINTING COMPONENT */}
              </Paper>
            </Grid>

            <HeroFeatures />
          </Grid>
        </Container>
      </Paper>
      
      {/* main content goes here */}
      <Container>
        <HomeContent />
      </Container>
      
      {/* PUT FOOTER HERE */}
      <Footer />
      <BackToTop />

      <Container style={{ padding: 0 }}>
        <Snackbar
          open={alertState.open}
          autoHideDuration={6000}
          onClose={() => setAlertState({ ...alertState, open: false })}
        >
          <Alert
            onClose={() => setAlertState({ ...alertState, open: false })}
            severity={alertState.severity}
          >
            {alertState.message}
          </Alert>
        </Snackbar>
      </Container>
    </div>
  );
};

const getCountdownDate = (
  candyMachine: CandyMachineAccount,
): Date | undefined => {
  if (
    candyMachine.state.isActive &&
    candyMachine.state.endSettings?.endSettingType.date
  ) {
    return toDate(candyMachine.state.endSettings.number);
  }

  return toDate(
    candyMachine.state.goLiveDate
      ? candyMachine.state.goLiveDate
      : candyMachine.state.isPresale
      ? new anchor.BN(new Date().getTime() / 1000)
      : undefined,
  );
};

export default Home;
